import { Moment } from "moment";
import { SystemMetadata } from "src/store/chat/api";

export type ChatSocketReceiveSystemMessageType = {
  message: {
    chat_id: string;
    datetime: string;
    id: string;
    type: MessageType.System;
    system_metadata: SystemMetadata;
  }
}

export type ChatSocketReceiveUserMessageType = {
  message: {
    chat_id: string;
    datetime: string;
    id: string;
    text: string;
    user_id: string;
  },
  user: {
    data: {
      avatar_link: string;
      name: string;
    },
    user_id: string;
  }
}

export type ChatSocketReceiveMessageType = ChatSocketReceiveUserMessageType | ChatSocketReceiveSystemMessageType;

export enum CurrentChatPage {
  DialogList,
  Dialog,
  Participants,
  ViewProfile,
  MessageUserViewProfile,
}

//uenum that keeps types of message
export enum MessageType {
  User = "user",
  System = "system",
}

//uenum that keeps types of dialog
export enum DialogType {
  Personal = "private",
  Group = "group",
}

export type UserMessage = {
  id: string;
  type: MessageType.User;
  userId?: string;
  avatarUrl?: string;
  caption?: string;
  text: React.ReactNode;
  datetime?: string;
};

export enum SystemMessageType {
  WelcomeToEvvve = "welcome_to_evvve",
  InvitationToGoLive = "invitation_to_go_live",
  TheEventStartsIn3Hours = "the_event_starts_in_3_hours",
  TheEventStartsIn15Min = "the_event_starts_in_15_min",
  TheEventIsLive = "the_event_is_live",
  IAmSpeakerAtTheEvent = "i_am_speaker_at_the_event",
  IAmOrganizingTheEvent = "i_am_organizing_the_event",
  IWillNotBeASpeakerAtTheEvent = "i_wont_be_a_speaker_at_the_event",
  IAcceptedToBeASpeakerAtTheEvent = "i_accepted_to_be_a_speaker_at_the_event",
  WelcomeToTheEvent = "welcome_to_the_event",
  TheEventHasBeenCancelled = "the_event_has_been_cancelled",
  NewSpeakerAtTheEvent = "new_speaker_at_the_event",
  EventDetailsHaveChanged = "event_details_have_changed",
  YouHaveBeenInvitedToBeASpeaker = "youve_been_invited_to_be_a_speaker",
  EventDurationExceeded = "event_duration_exceeded",
}

type _SystemMessage = {
  id: string;
  type: MessageType.System;
  systemMessageType: SystemMessageType;
  datetime?: string | null;
}

export interface EventDetailsHaveChangedMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.EventDetailsHaveChanged;
  eventId: string;
  eventTitle: string;
  newDate?: Moment;
  newStartTime?: Moment;
  newEndTime?: Moment;
}

export interface EventDurationExceededMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.EventDurationExceeded;
}

export interface WelcomeToTheEventMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.WelcomeToTheEvent;
  eventId: string;
  eventTitle: string;
}

export interface YouHaveBeenInvitedToBeASpeakerMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.YouHaveBeenInvitedToBeASpeaker;
  eventId: string;
  eventTitle: string;
  eventDate: Moment;
  organizerId: string;
}

export interface NewSpeakerAtTheEventMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.NewSpeakerAtTheEvent;
  eventId: string;
  eventTitle: string;
  speakerId: string;
}

export interface TheEventHasBeenCancelledMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.TheEventHasBeenCancelled;
  eventTitle: string;
}

export interface IAcceptedToBeASpeakerAtTheEventMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.IAcceptedToBeASpeakerAtTheEvent;
  eventId: string;
  eventTitle: string;
  eventDate: Moment;
  eventPrice: string;
}

export interface WelcomeToEvvveMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.WelcomeToEvvve;
}

export interface IAmSpeakerAtTheEventMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.IAmSpeakerAtTheEvent;
  eventId: string;
  eventTitle: string;
  eventDate: Moment;
}

export interface IWillNotBeASpeakerAtTheEventMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.IWillNotBeASpeakerAtTheEvent;
  eventId: string;
  eventTitle: string;
}

export interface IAmOrganizingTheEventMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.IAmOrganizingTheEvent;
  eventId: string;
  eventTitle: string;
  eventDate: Moment;
}

export interface InvitationToGoLiveMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.InvitationToGoLive;
  eventId: string;
  sessionId: string;
  senderId: string;
  cancelledBySpeaker: boolean;
  cancelledByOrganizer: boolean;
}

export interface TheEventStartsIn3HoursMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.TheEventStartsIn3Hours;
  eventId: string;
  eventTitle: string;
}

export interface TheEventStartsIn15MinMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.TheEventStartsIn15Min;
  eventId: string;
  eventTitle: string;
}

export interface TheEventIsLiveMessage extends _SystemMessage {
  systemMessageType: SystemMessageType.TheEventIsLive;
  eventId: string;
  eventTitle: string;
}

export type SystemMessage = WelcomeToEvvveMessage |
EventDurationExceededMessage |
YouHaveBeenInvitedToBeASpeakerMessage |
EventDetailsHaveChangedMessage |
NewSpeakerAtTheEventMessage |
TheEventHasBeenCancelledMessage |
WelcomeToTheEventMessage |
IAcceptedToBeASpeakerAtTheEventMessage |
IWillNotBeASpeakerAtTheEventMessage |
IAmSpeakerAtTheEventMessage |
IAmOrganizingTheEventMessage |
InvitationToGoLiveMessage |
TheEventStartsIn3HoursMessage |
TheEventStartsIn15MinMessage |
TheEventIsLiveMessage;

export type Message = UserMessage | SystemMessage;

type DialogLastUserMessage = {
  type: MessageType.User,
  username: string;
  text: string;
  datetime?: string;
};

type DialogLastSystemMessage = {
  type: MessageType.System,
  systemMessageType: SystemMessageType;
  datetime?: string;
};

export type DialogLastMessage = DialogLastUserMessage | DialogLastSystemMessage;

export interface Dialog {
  id?: string;
  type: DialogType;
  system: boolean;
  title?: string;
  firstUnreadMessageId?: string;
  lastMessage?: DialogLastMessage | null;
  unreadMessagesCount?: number;
  avatarUrl?: string;
  isOnline?: boolean;
  isVerified?: boolean;
  isWarning?: boolean;
  isRead?: boolean;
  isMute?: boolean;
  participantsCount?: number;
  followersCount?: number;
}
